import { useMemo } from 'react';
import { useCategoriesData, TIMELINE_YEARS } from './useCategoriesData';

const TYPE_VALUES_BY_FILTER = {
  income: ['Income'],
  expenses: ['Expenses'],
  all: ['Income', 'Expenses'],
};

function matchesType(bucket, typeFilter) {
  return TYPE_VALUES_BY_FILTER[typeFilter]?.includes(bucket.type) ?? true;
}

function filterByCurrency(items, currency) {
  if (currency === 'all') return items;
  return items.filter(d => d.currency === currency);
}

function getMonthName(date) {
  return date.toLocaleString('en-US', { month: 'long' });
}

function buildMonthDateRange(currentMonth) {
  if (!currentMonth.year || !currentMonth.month) return null;
  const lastDay = new Date(currentMonth.year, currentMonth.month, 0).getDate();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    start_date: `${currentMonth.year}-${pad(currentMonth.month)}-01`,
    end_date: `${currentMonth.year}-${pad(currentMonth.month)}-${pad(lastDay)}`,
  };
}

/**
 * Builds the full timeline grid (one entry per calendar month) by indexing the
 * raw buckets by `${year}-${month}`. Months with no activity yield empty `data`
 * arrays so the TimelineChart can still render gap markers.
 */
function buildMonthlyTimeline(buckets, currentMonth) {
  if (!currentMonth.year || !currentMonth.month) return [];

  const index = {};
  let minYear = currentMonth.year;
  buckets.forEach(b => {
    const key = `${b.year}-${b.month}`;
    if (!index[key]) index[key] = [];
    index[key].push(b);
    if (b.year < minYear) minYear = b.year;
  });

  const startYear = Math.min(minYear, currentMonth.year - 2);
  const timeline = [];
  for (let y = startYear; y <= currentMonth.year; y++) {
    for (let m = 1; m <= 12; m++) {
      timeline.push({ year: y, month: m, data: index[`${y}-${m}`] || [] });
    }
  }
  return timeline;
}

/**
 * Derives every view on the Categories page from a single bucket payload.
 *
 * Buckets come grouped by (year, month, currency, type, category_id). Filtering
 * by typeFilter/currency and splitting into current-month, rolling-6mo, yearly,
 * and historical views all happen here — the network layer only runs once.
 */
export function useCategoriesView({ typeFilter, selectedCurrency, searchTerm, displayCurrency = null }) {
  const {
    categories,
    monthlyBuckets: rawBuckets,
    currentMonth,
    numUnconvertible,
    periodCurrencies,
    loading,
    error,
    refetch,
    // Only while converting does the source-currency choice reach the server;
    // the per-currency view keeps slicing one payload in the client.
  } = useCategoriesData(displayCurrency, displayCurrency ? selectedCurrency : 'all');

  // Filter by type first — cheapest way to shrink everything downstream
  const bucketsByType = useMemo(
    () => rawBuckets.filter(b => matchesType(b, typeFilter)),
    [rawBuckets, typeFilter]
  );

  const currentMonthBuckets = useMemo(
    () => bucketsByType.filter(
      b => b.year === currentMonth.year && b.month === currentMonth.month
    ),
    [bucketsByType, currentMonth.year, currentMonth.month]
  );

  // Currencies that show up in the current month — used to gate the tabs.
  // Comes from /movements/filter-options rather than the bucket payload: the
  // tabs ask "which currencies did I spend in", which is independent of how the
  // totals happen to be aggregated. Deriving it from the buckets coupled it to
  // that aggregation, so it emptied out as soon as conversion collapsed the
  // currency axis.
  const tabCurrencies = useMemo(
    () => [...periodCurrencies].sort(),
    [periodCurrencies]
  );

  const effectiveCurrency = useMemo(() => {
    if (selectedCurrency !== 'all') return selectedCurrency;
    return tabCurrencies.length === 1 ? tabCurrencies[0] : 'all';
  }, [selectedCurrency, tabCurrencies]);

  // Which currency to slice the buckets by. While converting the slice already
  // happened server-side (the buckets carry the target currency, not the source),
  // so slicing again here would discard everything.
  const bucketCurrency = displayCurrency ? 'all' : effectiveCurrency;

  // Apply currency filter
  const spendingByCategory = useMemo(
    () => filterByCurrency(currentMonthBuckets, bucketCurrency),
    [currentMonthBuckets, bucketCurrency]
  );

  const monthlyTimeline = useMemo(() => {
    const timeline = buildMonthlyTimeline(bucketsByType, currentMonth);
    if (bucketCurrency === 'all') return timeline;
    return timeline.map(m => ({ ...m, data: filterByCurrency(m.data, bucketCurrency) }));
  }, [bucketsByType, currentMonth, bucketCurrency]);

  // 6 most recent completed months — excludes the current month, since it's
  // still in progress and would drag the average down (e.g. salary not yet
  // paid this month yields an artificially low rolling average).
  const recentSixMonths = useMemo(() => {
    const completed = monthlyTimeline.filter(
      m => m.year < currentMonth.year
        || (m.year === currentMonth.year && m.month < currentMonth.month)
    );
    return completed.slice(-6);
  }, [monthlyTimeline, currentMonth.year, currentMonth.month]);

  // 6-month average per (category, currency)
  const sixMoAvgByCatCurrency = useMemo(() => {
    if (recentSixMonths.length < 6) return {};
    const totals = {};
    recentSixMonths.forEach(m => {
      m.data.forEach(d => {
        if (d.category_id == null) return;
        const key = `${d.category_id}::${d.currency || 'COP'}`;
        totals[key] = (totals[key] || 0) + Math.abs(d.total || 0);
      });
    });
    const avgs = {};
    Object.keys(totals).forEach(key => { avgs[key] = totals[key] / 6; });
    return avgs;
  }, [recentSixMonths]);

  // How many of those months each (category, currency) pair was active in
  const activeMonthsByCatCurrency = useMemo(() => {
    const counts = {};
    recentSixMonths.forEach(m => {
      const keys = new Set(
        m.data.filter(d => d.category_id != null).map(d => `${d.category_id}::${d.currency || 'COP'}`)
      );
      keys.forEach(key => { counts[key] = (counts[key] || 0) + 1; });
    });
    return counts;
  }, [recentSixMonths]);

  // Yearly metrics for Key Metrics panel (matches the timeline range)
  const yearlyMetrics = useMemo(() => {
    if (!currentMonth.year) return [];
    const years = Array.from({ length: TIMELINE_YEARS }, (_, i) => currentMonth.year - i);
    return years.map(year => ({
      year,
      data: bucketsByType.filter(
        b => b.year === year && (effectiveCurrency === 'all' || b.currency === effectiveCurrency)
      ),
    }));
  }, [bucketsByType, currentMonth.year, effectiveCurrency]);

  // Build one row per (category, currency) pair, plus zero-rows for categories with no activity.
  // A zero-row still carries its 6-month average, in the one currency the category was most
  // often active in (ties go to the first alphabetically): one row per category, as before.
  const categoryRows = useMemo(() => {
    const validCategories = categories.filter(c => {
      if (typeFilter === 'all') return true;
      return c.type?.toLowerCase() === typeFilter.toLowerCase();
    });
    const catById = Object.fromEntries(validCategories.map(c => [c.id, c]));

    const rows = [];
    spendingByCategory.forEach(item => {
      const cat = catById[item.category_id];
      if (!cat) return;
      const currency = item.currency || 'COP';
      const spent = Math.abs(item.total || 0);
      rows.push({
        ...cat,
        currency,
        spent,
        isConverted: Boolean(item.is_converted),
        count: item.num_movements || 0,
        sixMonthAvg: sixMoAvgByCatCurrency[`${cat.id}::${currency}`] || 0,
      });
    });

    const idsWithSpending = new Set(spendingByCategory.map(i => i.category_id));
    validCategories.forEach(cat => {
      if (idsWithSpending.has(cat.id)) return;
      const prefix = `${cat.id}::`;
      const [usualKey] = Object.keys(sixMoAvgByCatCurrency)
        .filter(k => k.startsWith(prefix))
        .sort((a, b) => activeMonthsByCatCurrency[b] - activeMonthsByCatCurrency[a] || a.localeCompare(b));
      rows.push({
        ...cat,
        currency: usualKey ? usualKey.slice(prefix.length) : null,
        spent: 0,
        count: 0,
        sixMonthAvg: sixMoAvgByCatCurrency[usualKey] || 0,
      });
    });

    return rows.sort((a, b) => b.spent - a.spent);
  }, [categories, spendingByCategory, typeFilter, sixMoAvgByCatCurrency, activeMonthsByCatCurrency]);

  // Apply search filter
  const filteredRows = useMemo(() => {
    if (!searchTerm) return categoryRows;
    const s = searchTerm.toLowerCase();
    return categoryRows.filter(r =>
      r.name?.toLowerCase().includes(s) ||
      r.description?.toLowerCase().includes(s)
    );
  }, [categoryRows, searchTerm]);

  // Split into the two collapsible sections
  const rowsWithSpending = useMemo(
    () => filteredRows.filter(r => r.spent > 0),
    [filteredRows]
  );
  const rowsWithoutSpending = useMemo(
    () => filteredRows.filter(r => r.spent === 0),
    [filteredRows]
  );

  // Group rowsWithSpending by currency, ordered by per-currency total desc
  const rowsByCurrency = useMemo(() => {
    const groups = {};
    rowsWithSpending.forEach(r => {
      const c = r.currency || 'COP';
      if (!groups[c]) groups[c] = [];
      groups[c].push(r);
    });
    return Object.entries(groups)
      .map(([currency, rows]) => ({
        currency,
        rows,
        total: rows.reduce((s, r) => s + r.spent, 0),
      }))
      .sort((a, b) => b.total - a.total);
  }, [rowsWithSpending]);

  // Donut segments grouped by currency — one donut per currency to avoid mixing values
  const donutsByCurrency = useMemo(() => {
    const byCurrency = {};
    filteredRows.forEach(r => {
      if (r.spent <= 0 || r.id == null || !r.currency) return;
      if (!byCurrency[r.currency]) byCurrency[r.currency] = {};
      const cats = byCurrency[r.currency];
      if (!cats[r.id]) cats[r.id] = { id: r.id, name: r.name, icon: r.icon, color: r.color, spent: 0 };
      cats[r.id].spent += r.spent;
    });
    return Object.entries(byCurrency)
      .map(([currency, cats]) => {
        const segments = Object.values(cats).sort((a, b) => b.spent - a.spent);
        const total = segments.reduce((s, c) => s + c.spent, 0);
        return {
          currency,
          total,
          segments: segments.map(s => ({ ...s, share: total > 0 ? s.spent / total : 0 })),
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [filteredRows]);

  // Summary card totals — per currency
  const totalsByCurrency = useMemo(() => {
    const totals = {};
    spendingByCategory.forEach(item => {
      const c = item.currency || 'COP';
      totals[c] = (totals[c] || 0) + Math.abs(item.total || 0);
    });
    return totals;
  }, [spendingByCategory]);

  // MI-329: whether the converted totals converted anything. Months of pesos
  // shown in pesos are exact, and are not marked "≈".
  const totalsConverted = useMemo(
    () => spendingByCategory.some(item => item.is_converted),
    [spendingByCategory]
  );
  const sixMoConverted = useMemo(
    () => recentSixMonths.some(m => m.data.some(d => d.is_converted)),
    [recentSixMonths]
  );

  const sixMoAvgByCurrency = useMemo(() => {
    if (recentSixMonths.length < 6) return {};
    // Per currency, the currencies the tabs list (the last 12 months). Converted, every
    // bucket carries the display currency, which the tabs do not list.
    const listed = displayCurrency ? null : new Set(tabCurrencies);
    const totals = {};
    recentSixMonths.forEach(m => {
      m.data.forEach(d => {
        const c = d.currency || 'COP';
        if (listed && !listed.has(c)) return;
        totals[c] = (totals[c] || 0) + Math.abs(d.total || 0);
      });
    });
    const avgs = {};
    Object.keys(totals).forEach(c => { avgs[c] = totals[c] / 6; });
    return avgs;
  }, [recentSixMonths, tabCurrencies, displayCurrency]);

  const currentMonthName = currentMonth.year && currentMonth.month
    ? getMonthName(new Date(currentMonth.year, currentMonth.month - 1, 1))
    : getMonthName(new Date());

  const monthDateRange = useMemo(() => buildMonthDateRange(currentMonth), [currentMonth]);

  return {
    loading,
    error,
    refetch,
    categories,
    tabCurrencies,
    effectiveCurrency,
    rowsWithSpending,
    rowsByCurrency,
    rowsWithoutSpending,
    filteredRows,
    donutsByCurrency,
    totalsByCurrency,
    sixMoAvgByCurrency,
    sixMoAvgByCatCurrency,
    totalsConverted,
    sixMoConverted,
    currentMonthName,
    monthDateRange,
    monthlyTimeline,
    yearlyMetrics,
    numUnconvertible,
  };
}
